import { Card } from '../atoms/Card/Card';
import { Badge } from '../atoms/Badge/Badge';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { Button } from '../atoms/Button/Button';
import { Stack } from '../atoms/Stack/Stack';
import { Inline } from '../atoms/Inline/Inline';
import { useTranslation } from '../../i18n/useTranslation';
import './CameraSourceCard.css';

export type CameraSourceStatus = 'active' | 'revoked';

export interface CameraSourceCardProps {
  readonly id: string;
  readonly name: string;
  readonly status: CameraSourceStatus;
  readonly lastReceivedImage?: {
    readonly url: string;
    /** ISO-8601 timestamp of when the image was captured. */
    readonly capturedAt: string;
  };
  readonly expectedCaptureTimes: readonly string[];
  readonly onEdit?: (id: string) => void;
  readonly onRevoke?: (id: string) => void;
  readonly onDelete?: (id: string) => void;
}

function formatCapturedAt(capturedAt: string, language: string): string {
  const date = new Date(capturedAt);
  if (Number.isNaN(date.getTime())) return capturedAt;
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/**
 * Presentational card for a single camera source (design spec #1436,
 * "Camera Sources Settings" populated/partial card). Renders purely from props:
 * - active: status badge, last received image (or "no image yet"), expected
 *   capture times (or "none configured"), Edit + Revoke actions;
 * - revoked: revoked badge and a single Delete action (no re-activation).
 */
export function CameraSourceCard({
  id,
  name,
  status,
  lastReceivedImage,
  expectedCaptureTimes,
  onEdit,
  onRevoke,
  onDelete,
}: CameraSourceCardProps) {
  const { t, i18n } = useTranslation();
  const isRevoked = status === 'revoked';
  const hasTimes = expectedCaptureTimes.length > 0;

  return (
    <Card className="camera-source-card">
      <Stack gap="3">
        <Inline justify="between" align="center">
          <Heading level={3}>{name}</Heading>
          {isRevoked ? (
            <Badge tone="neutral">{t('cameraSources.status.revoked')}</Badge>
          ) : (
            <Badge tone="success">{t('cameraSources.status.active')}</Badge>
          )}
        </Inline>

        {lastReceivedImage ? (
          <>
            <Text variant="muted">{t('cameraSources.lastReceived.label')}</Text>
            <Inline gap="3" align="center">
              <img
                className="camera-source-card__thumbnail"
                src={lastReceivedImage.url}
                alt={t('cameraSources.lastImage.alt')}
              />
              <Stack gap="1" className="camera-source-card__thumbnail-meta">
                <Text>
                  <time dateTime={lastReceivedImage.capturedAt}>
                    {formatCapturedAt(lastReceivedImage.capturedAt, i18n.language)}
                  </time>
                </Text>
                <Text variant="muted">{t('cameraSources.source.camera')}</Text>
              </Stack>
            </Inline>
          </>
        ) : (
          <Text variant="muted">{t('cameraSources.noImageYet')}</Text>
        )}

        {hasTimes ? (
          <>
            <Text variant="muted">{t('cameraSources.expectedTimes.label')}</Text>
            <Inline gap="2">
              {expectedCaptureTimes.map(time => (
                <Badge key={time} tone="info">
                  {time}
                </Badge>
              ))}
            </Inline>
          </>
        ) : (
          <Text variant="muted">{t('cameraSources.expectedTimes.none')}</Text>
        )}

        <Inline gap="2">
          {isRevoked ? (
            <Button variant="secondary" onClick={() => onDelete?.(id)}>
              {t('cameraSources.delete')}
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => onEdit?.(id)}>
                {t('cameraSources.edit')}
              </Button>
              <Button variant="danger" onClick={() => onRevoke?.(id)}>
                {t('cameraSources.revoke')}
              </Button>
            </>
          )}
        </Inline>
      </Stack>
    </Card>
  );
}

export default CameraSourceCard;
